<script>
import ActivityDetails from './activity-details.vue';

export default {
    components: {
        ActivityDetails
    },
    props: {
        activity: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            showFullDescription: false
        };
    },
    computed: {
        activityName() {
            return this.activity?.content?.name || this.activity?.name || 'Sin nombre';
        },
        activityDescription() {
            const description = this.activity?.content?.description || '';
            // Remove HTML tags for display
            const cleanDescription = description.replace(/<[^>]*>/g, '');
            return cleanDescription;
        },
        truncatedDescription() {
            if (this.showFullDescription) {
                return this.activityDescription;
            }
            return this.activityDescription.length > 200 
                ? this.activityDescription.substring(0, 200) + '...' 
                : this.activityDescription;
        },
        featuredImage() {
            const images = this.activity?.content?.media?.images || [];
            if (images.length === 0) return null;
            
            // Get the first image with LARGE size
            const image = images[0];
            const largeUrl = image.urls?.find(url => url.sizeType === 'LARGE');
            return largeUrl?.resource || image.urls?.[0]?.resource;
        },
        operationDaysText() {
            const days = this.activity?.operationDays || [];
            if (days.length === 0) return 'No especificado';
            
            const dayNames = days.map(day => day.name);
            if (dayNames.length === 7) return 'Todos los días';
            if (dayNames.length === 5 && dayNames.includes('Monday') && dayNames.includes('Friday')) {
                return 'Lunes a Viernes';
            }
            if (dayNames.length === 2 && dayNames.includes('Saturday') && dayNames.includes('Sunday')) {
                return 'Fines de semana';
            }
            
            return dayNames.join(', ');
        },
        countryInfo() {
            const country = this.activity?.country;
            if (!country) return '';
            
            const destinations = country.destinations?.map(d => d.name).join(', ');
            return `${country.name}${destinations ? ` - ${destinations}` : ''}`;
        }
    },
    methods: {
        toggleDescription() {
            this.showFullDescription = !this.showFullDescription;
        },
        formatPrice(amount) {
            if (amount === 0) return 'Gratis';
            return new Intl.NumberFormat('es-ES', {
                style: 'currency',
                currency: this.activity.currency || 'EUR'
            }).format(amount);
        },
        getPaxTypeLabel(paxType) {
            const labels = {
                'ADULT': 'Adulto',
                'CHILD': 'Niño',
                'SENIOR': 'Senior',
                'INFANT': 'Bebé'
            };
            return labels[paxType] || paxType;
        },
        getAgeRangeText(ageFrom, ageTo) {
            if (ageFrom === ageTo) {
                return `${ageFrom} años`;
            }
            return `${ageFrom}-${ageTo} años`;
        },
        async copyToClipboard(text) {
            try {
                // Use modern Clipboard API
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(text);
                    this.showCopySuccess();
                } else {
                    // For non-secure contexts, show a message to the user
                    this.showCopyInstructions(text);
                }
            } catch (err) {
                console.error('Error copying to clipboard:', err);
                this.showCopyInstructions(text);
            }
        },
        showCopySuccess() {
            this.$bvToast.toast('Código copiado al portapapeles', {
                title: 'Éxito',
                variant: 'success',
                solid: true,
                autoHideDelay: 2000
            });
        },
        showCopyInstructions(text) {
            this.$bvToast.toast(`Código: ${text} - Selecciona y copia manualmente`, {
                title: 'Copia manual requerida',
                variant: 'warning',
                solid: true,
                autoHideDelay: 5000
            });
        }
    }
};
</script>

<template>
    <div class="card activity-card">
        <div class="card-body p-0">
            <div class="row no-gutters">
                <!-- Left Column: Activity Information -->
                <div class="col-md-5 activity-info-column">
                    <div class="activity-info-content">
                        <!-- Activity Image -->
                        <div class="activity-image-container">
                            <img 
                                v-if="featuredImage" 
                                :src="featuredImage" 
                                :alt="activityName"
                                class="activity-image"
                            />
                            <div v-else class="no-image-placeholder">
                                <i class="mdi mdi-image text-muted" style="font-size: 3rem;"></i>
                                <p class="text-muted mt-2">Sin imagen</p>
                            </div>
                        </div>

                        <!-- Activity Header -->
                        <div class="activity-header">
                            <h5 class="activity-title">{{ activityName }}</h5>
                            <div class="activity-location text-muted">
                                <i class="mdi mdi-map-marker mr-1"></i>
                                {{ countryInfo }}
                            </div>
                            <!-- Activity Code -->
                            <div class="activity-code-section">
                                <code class="activity-code">{{ activity.activityCode || activity.code }}</code>
                                <button 
                                    @click="copyToClipboard(activity.activityCode || activity.code)"
                                    class="btn btn-sm btn-link copy-btn"
                                    v-b-tooltip.hover 
                                    title="Copiar código"
                                >
                                    <i class="mdi mdi-content-copy"></i>
                                </button>
                            </div>
                        </div>

                        <!-- Activity Description -->
                        <div class="activity-description">
                            <p class="description-text">{{ truncatedDescription }}</p>
                            <button 
                                v-if="activityDescription.length > 200"
                                @click="toggleDescription"
                                class="btn btn-link btn-sm p-0 text-primary"
                            >
                                {{ showFullDescription ? 'Ver menos' : 'Ver más' }}
                            </button>
                        </div>

                        <!-- Operation Days -->
                        <div class="operation-days">
                            <small class="text-muted">
                                <i class="mdi mdi-calendar-clock mr-1"></i>
                                <strong>Días de operación:</strong> {{ operationDaysText }}
                            </small>
                        </div>

                        <!-- Activity Details (shown when description is expanded) -->
                        <ActivityDetails 
                            :activity="activity"
                            :show="showFullDescription"
                        />
                    </div>
                </div>

                <!-- Right Column: Modalities -->
                <div class="col-md-7 modalities-column">
                    <div class="modalities-content">
                        <h6 class="modalities-title">
                            <i class="mdi mdi-ticket-confirmation mr-2"></i>
                            Modalidades disponibles
                        </h6>
                        
                        <div class="modalities-list">
                            <div 
                                v-for="modality in activity.modalities" 
                                :key="modality.code"
                                class="modality-item"
                            >
                                <div class="modality-header">
                                    <div class="modality-info">
                                        <h6 class="modality-name">{{ modality.name }}</h6>
                                        <div class="modality-code-section">
                                            <code class="modality-code">{{ modality.code }}</code>
                                            <button 
                                                @click="copyToClipboard(modality.code)"
                                                class="btn btn-sm btn-link copy-btn"
                                                v-b-tooltip.hover 
                                                title="Copiar código"
                                            >
                                                <i class="mdi mdi-content-copy"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <span class="modality-duration badge badge-info">
                                        <i class="mdi mdi-clock mr-1"></i>
                                        {{ modality.duration?.value }} {{ modality.duration?.metric?.toLowerCase() }}
                                    </span>
                                </div>

                                <!-- Pricing Table -->
                                <div class="pricing-table">
                                    <div class="table-responsive">
                                        <table class="table table-sm table-borderless">
                                            <thead>
                                                <tr>
                                                    <th class="text-muted small">Tipo</th>
                                                    <th class="text-muted small">Edad</th>
                                                    <th class="text-muted small">Precio</th>
                                                    <th class="text-muted small">Taquilla</th>
                                                    <th class="text-muted small">Obligatorio</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="amount in modality.amountsFrom" :key="`${modality.code}-${amount.paxType}-${amount.ageFrom}`">
                                                    <td>
                                                        <span class="badge" :class="{
                                                            'badge-primary': amount.paxType === 'ADULT',
                                                            'badge-success': amount.paxType === 'CHILD',
                                                            'badge-warning': amount.paxType === 'SENIOR',
                                                            'badge-info': amount.paxType === 'INFANT'
                                                        }">
                                                            {{ getPaxTypeLabel(amount.paxType) }}
                                                        </span>
                                                    </td>
                                                    <td class="small">{{ getAgeRangeText(amount.ageFrom, amount.ageTo) }}</td>
                                                    <td class="font-weight-bold">{{ formatPrice(amount.amount) }}</td>
                                                    <td class="text-muted">{{ formatPrice(amount.boxOfficeAmount) }}</td>
                                                    <td>
                                                        <i 
                                                            v-if="amount.mandatoryApplyAmount" 
                                                            class="mdi mdi-check text-success"
                                                            v-b-tooltip.hover 
                                                            title="Precio obligatorio"
                                                        ></i>
                                                        <i 
                                                            v-else 
                                                            class="mdi mdi-close text-muted"
                                                            v-b-tooltip.hover 
                                                            title="Precio opcional"
                                                        ></i>
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.activity-card {
    transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    border: 1px solid #e9ecef;
    min-height: 400px;
}

.activity-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* Left Column: Activity Information */
.activity-info-column {
    border-right: 1px solid #e9ecef;
    background-color: #fafbfc;
}

.activity-info-content {
    padding: 1.5rem;
    height: 100%;
    display: flex;
    flex-direction: column;
}

.activity-image-container {
    height: 200px;
    overflow: hidden;
    position: relative;
    border-radius: 0.375rem;
    margin-bottom: 1rem;
}

.activity-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.no-image-placeholder {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background-color: #f8f9fa;
}

.activity-header {
    margin-bottom: 1rem;
}

.activity-title {
    font-size: 1.2rem;
    font-weight: 600;
    line-height: 1.3;
    margin-bottom: 0.5rem;
    color: #2c3e50;
}

.activity-location {
    font-size: 0.9rem;
    color: #6c757d;
    margin-bottom: 0.75rem;
}

.activity-code-section {
    margin-top: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

.activity-code {
    font-family: 'Courier New', monospace;
    font-size: 0.75rem;
    color: #6c757d;
    background: none;
    padding: 0;
    border: none;
}

.copy-btn {
    padding: 0;
    font-size: 0.7rem;
    min-width: auto;
    color: #6c757d;
    border: none;
    background: none;
}

.copy-btn:hover {
    color: #007bff;
    background: none;
    border: none;
}

.activity-description {
    flex-grow: 1;
    margin-bottom: 1rem;
}

.description-text {
    font-size: 0.9rem;
    line-height: 1.6;
    color: #495057;
    margin-bottom: 0.5rem;
}

.operation-days {
    margin-top: auto;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
}

/* Right Column: Modalities */
.modalities-column {
    background-color: white;
}

.modalities-content {
    padding: 1.5rem;
    height: 100%;
    overflow-y: auto;
}

.modalities-title {
    font-size: 1.1rem;
    font-weight: 600;
    color: #495057;
    border-bottom: 2px solid #007bff;
    padding-bottom: 0.75rem;
    margin-bottom: 1.5rem;
}

.modalities-list {
    max-height: calc(100vh - 300px);
    overflow-y: auto;
}

.modality-item {
    border: 1px solid #e9ecef;
    border-radius: 0.375rem;
    padding: 1rem;
    background-color: #f8f9fa;
    margin-bottom: 1rem;
}

.modality-item:last-child {
    margin-bottom: 0;
}

.modality-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.modality-info {
    flex: 1;
    min-width: 0;
}

.modality-name {
    font-size: 0.95rem;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    color: #495057;
}

.modality-code-section {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin-top: 0.25rem;
}

.modality-code {
    font-family: 'Courier New', monospace;
    font-size: 0.7rem;
    color: #6c757d;
    background: none;
    padding: 0;
    border: none;
}

.modality-duration {
    font-size: 0.8rem;
    white-space: nowrap;
}

.pricing-table {
    background-color: white;
    border-radius: 0.25rem;
    padding: 0.5rem;
    border: 1px solid #dee2e6;
}

.pricing-table .table {
    margin-bottom: 0;
}

.pricing-table th {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 1px solid #dee2e6;
    padding: 0.5rem 0.25rem;
    background-color: #f8f9fa;
}

.pricing-table td {
    padding: 0.5rem 0.25rem;
    vertical-align: middle;
    font-size: 0.85rem;
    border-bottom: 1px solid #f1f3f4;
}

.pricing-table tr:last-child td {
    border-bottom: none;
}

.badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
}

.btn-link {
    text-decoration: none;
    font-size: 0.85rem;
}

.btn-link:hover {
    text-decoration: underline;
}

/* Responsive adjustments */
@media (max-width: 767.98px) {
    .activity-info-column {
        border-right: none;
        border-bottom: 1px solid #e9ecef;
    }
    
    .activity-info-content,
    .modalities-content {
        padding: 1rem;
    }
    
    .activity-image-container {
        height: 150px;
    }
    
    .modalities-list {
        max-height: none;
    }
    
    .activity-code-section,
    .modality-code-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
    }
    
    .copy-btn {
        align-self: flex-start;
        width: fit-content;
    }
}
</style> 